import {styled} from '@linaria/react';
import React from 'react';
import {COLORS} from '../../../constants/colors';
import {
  mediaDesktopAndUp,
  mediaMobileOnly,
  tabletAndUpLinaria,
} from '../../../constants/customMedia';
import {FONT_SIZES, FONT_WEIGHTS} from '../../../constants/typography';
import {Text} from '../../atoms/Text/Text';
import {
  HeaderTitle,
  HorizontalSlider,
  Header as HorizontalSliderHeader,
  HorizontalSliderProps,
} from '../../molecules/HorizontalSlider/HorizontalSlider';

interface TeaserSliderProps {
  constrained?: boolean;
  headline?: string;
  subline?: string;
  testId?: string;
  sliderRef?: React.RefObject<HTMLElement | null>;
  uniqueKey?: string;
  className?: string;
  moveTitleOnTheSide?: boolean;
  onSliderIndexChange?: (index: number) => void;
}

export const TeaserSlider: React.FC<
  React.PropsWithChildren<TeaserSliderProps & Partial<HorizontalSliderProps>>
> = ({
  constrained,
  headline,
  subline,
  testId: TeaserSliderTestId = 'teaserSlider',
  sliderRef,
  uniqueKey,
  children,
  className,
  moveTitleOnTheSide,
  onSliderIndexChange,
  ...rest
}) => {
  return (
    <StyledHorizontalSlider
      className={className}
      ref={sliderRef}
      dataTestId={TeaserSliderTestId}
      moveTitleOnTheSide={moveTitleOnTheSide}
      onIndexChange={onSliderIndexChange}
      title={
        <TitleWrapper as="h2">
          {subline && (
            <Subline
              data-testid={`${TeaserSliderTestId}Subline`}
              title={subline}
            >
              {subline}
            </Subline>
          )}
          {headline && (
            <Headline
              data-testid={`${TeaserSliderTestId}Headline`}
              title={headline}
            >
              {headline}
            </Headline>
          )}
        </TitleWrapper>
      }
      constrained={constrained}
      hideSideButtons
      hasHeaderBottomGap
      uniqueKey={uniqueKey}
      {...rest}
    >
      {children}
    </StyledHorizontalSlider>
  );
};

const StyledHorizontalSlider = styled(HorizontalSlider)<{
  moveTitleOnTheSide?: boolean;
}>`
  margin-top: ${props => (props.moveTitleOnTheSide ? '24px' : '')};
  --height-adjustment: 0;

  @media ${mediaDesktopAndUp} {
    margin-top: 24px;

    --height-adjustment: ${props => (props.moveTitleOnTheSide ? '8rem' : 0)};
  }

  @supports (content-visibility: auto) {
    contain: content;
  }

  --min-height-s: calc(350px - var(--height-adjustment));
  --min-height-m: calc(400px - var(--height-adjustment));
  --min-height-l: calc(450px - var(--height-adjustment));
  --min-height-xl: calc(500px - var(--height-adjustment));

  min-height: var(--min-height-s);

  @media only screen and (min-width: 1350px) {
    min-height: var(--min-height-m);
  }

  @media only screen and (min-width: 1550px) {
    min-height: var(--min-height-l);
  }

  @media only screen and (min-width: 1700px) {
    min-height: var(--min-height-xl);
  }

  ${HeaderTitle} {
    align-items: center;
    flex: 1;
    flex-direction: column;

    ${tabletAndUpLinaria('align-items: flex-start;')};
  }

  @media ${mediaMobileOnly} {
    ${HorizontalSliderHeader} {
      margin: 0 16px 16px;
    }
  }
`;

const Headline = styled(Text)`
  font-size: ${FONT_SIZES.px16};
  font-weight: ${FONT_WEIGHTS.mediumHeavy};
  line-height: 1.25;
  margin: 0;

  ${tabletAndUpLinaria(`
    font-weight: 800;
    font-size: ${FONT_SIZES.px32};
  `)}
`;

const Subline = styled(Text)`
  font-weight: ${FONT_WEIGHTS.mediumHeavy};
  font-size: ${FONT_SIZES.px12};
  color: ${COLORS.Grey1};
  text-transform: uppercase;
  line-height: 1.5;
  margin: 0;
  letter-spacing: 0.48px;
`;

const TitleWrapper = styled(Text)`
  display: flex;
  flex-direction: column;
  text-align: center;

  ${tabletAndUpLinaria(`
    text-align: unset;
    gap: 8px;
  `)}
`;
